import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../api/axiosInstance";
import { businessScopeParams } from "../utils/businessScope";
import NavigationBar from "../layout/NavigationBar";
import Footer from "../layout/footer";
import useShippingStatus from "../hooks/useShippingStatus";
import {
    orderTab,
    shippingChip,
    statusDetail,
    orderAttention,
    hasShipment,
    isCod,
    rupees,
    openLabel,
    fetchLabels,
    orderNumber,
    isSellerSession,
    shipmentApi,
    SHIPMENT_TIMEOUT,
    pickupDays,
    PICKUP_SLOTS,
} from "./orderDetails";

/**
 * Order Management (eventseasyadmin#299): the store's orders by shipping
 * status - To ship, Shipped, Delivered, Needs attention, Returned,
 * Cancelled - with the courier and AWB (or tracking number), and what to do
 * next on each. A courier store books, labels and schedules pickups for
 * several orders at once, one order at a time through uniquegiftapi's
 * shipment APIs (#298 APIs 7-9: /shipping/orders/:orderId/...).
 * Orders: GET /event/business/list. Styling: AdminTheme.css .mtk-orders-*.
 */

const PAGE_SIZES = [10, 25, 50, 100];
const PAGE_SIZE_KEY = "orderManagement_pageLength";

const savedPageSize = () => {
    try {
        const n = Number(localStorage.getItem(PAGE_SIZE_KEY));
        return PAGE_SIZES.includes(n) ? n : 10;
    } catch (e) {
        return 10;
    }
};

// The same number as on the order's page (orderDetails.orderNumber): this order's own id
const orderCode = (order) => orderNumber(order._id);
const customerOf = (order) => order.user_name || order.event_name || [order.first_name, order.last_name].filter(Boolean).join(" ") || "—";
const totalOf = (order) => (Number(order.purchased_price) > 0 ? Number(order.purchased_price) : Number(order.total_price) || 0);
const deliverTo = (order) => [order.shipping_city || order.city, order.shipping_zip_code || order.zip_code].filter(Boolean).join(", ");

function OrderManagement() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const shipping = useShippingStatus();
    const courierStore = !!shipping.isCourier;
    // The courier actions need the store's own login (a Super Admin sees them read-only)
    const seller = isSellerSession();
    const courierActions = courierStore && seller;

    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const [tab, setTab] = useState("all");
    const [search, setSearch] = useState("");
    const [pageSize, setPageSize] = useState(savedPageSize);
    const [page, setPage] = useState(0);
    const [selected, setSelected] = useState(() => new Set());
    const [busy, setBusy] = useState(false);
    const [notice, setNotice] = useState(null);
    const [pickupOpen, setPickupOpen] = useState(false);
    const days = useMemo(() => pickupDays(), []);
    const [pickupDay, setPickupDay] = useState(days[1].value);
    const [pickupSlot, setPickupSlot] = useState("10-14");

    const load = async () => {
        setLoadError(false);
        try {
            const res = await api.get("/event/business/list", { params: businessScopeParams() });
            const events = (res.data && res.data.result && res.data.result[0] && res.data.result[0].events) || [];
            setOrders([...events].sort((a, b) => new Date(b.created_date) - new Date(a.created_date)));
        } catch (e) {
            console.error("Error fetching orders:", e);
            setLoadError(true);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const TABS = [
        { key: "all", label: t("orderManagement.list.tabAll", "All") },
        { key: "toShip", label: t("orderManagement.list.tabToShip", "To ship") },
        { key: "shipped", label: t("orderManagement.list.tabShipped", "Shipped") },
        { key: "delivered", label: t("orderManagement.list.tabDelivered", "Delivered") },
        ...(courierStore ? [{ key: "attention", label: t("orderManagement.list.tabAttention", "Needs attention") }] : []),
        { key: "returned", label: courierStore ? t("orderManagement.list.tabRto", "Returned (RTO)") : t("orderManagement.list.tabReturned", "Returned") },
        { key: "cancelled", label: t("orderManagement.list.tabCancelled", "Cancelled") },
    ];

    const inTab = (order, key) => {
        if (key === "all") return true;
        if (key === "attention") return !!orderAttention(order);
        return orderTab(order) === key;
    };
    const counts = Object.fromEntries(TABS.map(({ key }) => [key, orders.filter((o) => inTab(o, key)).length]));

    const query = search.trim().toLowerCase();
    const shown = orders.filter((o) => inTab(o, tab)).filter((o) => !query || [
        orderCode(o), o.order_id || "", customerOf(o), (o.shipment && o.shipment.awb) || "", o.tracking_number || "",
    ].some((v) => String(v).toLowerCase().includes(query)));
    const pages = Math.max(1, Math.ceil(shown.length / pageSize));
    const current = Math.min(page, pages - 1);
    const rows = shown.slice(current * pageSize, current * pageSize + pageSize);

    const chooseTab = (key) => {
        setTab(key);
        setPage(0);
        setSelected(new Set());
        setNotice(null);
    };
    const toggle = (id) => setSelected((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
    });
    const allOnPage = rows.length > 0 && rows.every((o) => selected.has(o._id));
    const togglePage = () => setSelected((prev) => {
        const next = new Set(prev);
        rows.forEach((o) => (allOnPage ? next.delete(o._id) : next.add(o._id)));
        return next;
    });

    const chosen = orders.filter((o) => selected.has(o._id));
    const toCreate = chosen.filter((o) => orderTab(o) === "toShip");
    const withLabel = chosen.filter((o) => hasShipment(o) && !/^RTO_/.test(o.shipment.status));
    const forPickup = chosen.filter((o) => hasShipment(o) && ["CREATED", "PICKUP_SCHEDULED"].includes(o.shipment.status));

    const failure = (error, fallback) => {
        const data = (error && error.response && error.response.data) || {};
        return data.message || error.message || fallback;
    };

    const createShipments = async (list) => {
        setBusy(true);
        setNotice(null);
        try {
            // One at a time; once the courier refuses the token, the rest would fail the same way
            const results = [];
            for (const o of list) {
                if (results.some((r) => r.code === "auth")) {
                    results.push({ event_id: o._id, ok: false, code: "auth", error: results.find((r) => r.code === "auth").error });
                    continue;
                }
                try {
                    await api.post(shipmentApi(o._id, "shipment"), {}, { timeout: SHIPMENT_TIMEOUT });
                    results.push({ event_id: o._id, ok: true });
                } catch (err) {
                    const data = (err.response && err.response.data) || {};
                    results.push({ event_id: o._id, ok: false, code: data.code, error: data.message || "Couldn't create the shipment" });
                }
            }
            setNotice(batchNotice(results, {
                allOk: (n) => t("orderManagement.list.shipmentsCreated", n === 1 ? "Shipment created." : "{{count}} shipments created.", { count: n }),
                some: (ok, n) => t("orderManagement.list.shipmentsCreatedSome", "{{ok}} of {{count}} shipments created.", { ok, count: n }),
            }));
            setSelected(new Set());
            await load();
        } catch (error) {
            setNotice({ type: "danger", text: failure(error, t("orderManagement.list.shipmentsFailed", "Couldn't create the shipments. Please try again.")) });
        } finally {
            setBusy(false);
        }
    };

    // One order's result per line: "#298D34: why"
    const byId = new Map(orders.map((o) => [o._id, o]));
    const lineFor = (r) => `#${orderCode(byId.get(r.event_id) || { _id: r.event_id })}: ${r.error}`;
    const batchNotice = (results, text) => {
        const failed = results.filter((r) => !r.ok);
        const ok = results.length - failed.length;
        if (!failed.length) return { type: "success", text: text.allOk(results.length) };
        return { type: ok ? "warning" : "danger", text: text.some(ok, results.length), list: failed.map(lineFor) };
    };

    const downloadLabels = async (list) => {
        // One order: its label opens in a new tab. Several: browsers allow one
        // new tab per click, so their links are listed here to open each one.
        if (list.length === 1) {
            setNotice(null);
            try {
                const { url, blocked } = await openLabel(list[0]._id);
                if (blocked) {
                    setNotice({ type: "warning", text: t("orderManagement.list.labelBlocked", "Your browser blocked the new tab. Open the label here:"), links: [{ label: `#${orderCode(list[0])}`, url }] });
                }
            } catch (error) {
                setNotice({ type: "danger", text: failure(error, t("orderManagement.list.labelsFailed", "Couldn't get the labels. Please try again.")) });
            }
            return;
        }
        setBusy(true);
        setNotice(null);
        try {
            const results = await fetchLabels(list.map((o) => o._id));
            const ready = results.filter((r) => r.url);
            setNotice({
                type: ready.length === results.length ? "success" : ready.length ? "warning" : "danger",
                text: ready.length
                    ? t("orderManagement.list.labelsReady", "{{ok}} of {{count}} labels ready. Open each one:", { ok: ready.length, count: results.length })
                    : t("orderManagement.list.labelsFailed", "Couldn't get the labels. Please try again."),
                links: ready.map((r) => ({ label: `#${orderCode(byId.get(r.id) || { _id: r.id })}`, url: r.url })),
                list: results.filter((r) => !r.url).map((r) => lineFor({ event_id: r.id, error: r.error })),
            });
        } finally {
            setBusy(false);
        }
    };

    const schedulePickup = async () => {
        setBusy(true);
        setNotice(null);
        try {
            // Each parcel's own result: one that fails doesn't hide the ones already
            // scheduled. (The API joins the day's pickup for the store's other
            // parcels, so the courier gets one request.)
            const results = [];
            for (const o of forPickup) {
                try {
                    // parcels: the courier's one pickup request expects them all
                    await api.post(shipmentApi(o._id, "pickup"), { date: pickupDay, slot: pickupSlot, parcels: forPickup.length });
                    results.push({ event_id: o._id, ok: true });
                } catch (err) {
                    results.push({ event_id: o._id, ok: false, error: failure(err, t("orderManagement.list.pickupFailed", "Couldn't schedule the pickup. Please try again.")) });
                }
            }
            const when = `${days.find((d) => d.value === pickupDay).label}, ${PICKUP_SLOTS[pickupSlot]}`;
            setNotice(batchNotice(results, {
                allOk: (n) => t("orderManagement.list.pickupScheduled", n === 1 ? "Pickup scheduled for 1 parcel: {{when}}." : "Pickup scheduled for {{count}} parcels: {{when}}.", { count: n, when }),
                some: (ok, n) => t("orderManagement.list.pickupScheduledSome", "Pickup scheduled for {{ok}} of {{count}} parcels: {{when}}.", { ok, count: n, when }),
            }));
            if (results.some((r) => r.ok)) {
                setPickupOpen(false);
                setSelected(new Set());
                await load();
            }
        } finally {
            setBusy(false);
        }
    };

    const view = (order) => navigate(`/ordermanagement_view?id=${order._id}`);

    const actionFor = (order) => {
        const tabKey = orderTab(order);
        const s = order.shipment || {};
        if (tabKey === "toShip") {
            if (courierStore && !seller) return null;
            return courierStore
                ? <button type="button" className="mtk-orders-btn is-primary" disabled={busy} onClick={() => createShipments([order])}>{t("orderManagement.list.createShipment", "Create shipment")}</button>
                : <button type="button" className="mtk-orders-btn is-primary" onClick={() => view(order)}>{t("orderManagement.list.markShipped", "Mark as shipped")}</button>;
        }
        if (seller && hasShipment(order) && ["CREATED", "PICKUP_SCHEDULED"].includes(s.status)) {
            return <button type="button" className="mtk-orders-btn" disabled={busy} onClick={() => downloadLabels([order])}>{t("orderManagement.list.downloadLabel", "Download label")}</button>;
        }
        if (tabKey === "shipped" && hasShipment(order) && order.courier_tracking_url) {
            return <a className="mtk-orders-btn" href={order.courier_tracking_url} target="_blank" rel="noopener noreferrer">{t("orderManagement.list.track", "Track")}</a>;
        }
        if (tabKey === "shipped" && !hasShipment(order)) {
            return <button type="button" className="mtk-orders-btn" onClick={() => view(order)}>{t("orderManagement.list.updateStatus", "Update status")}</button>;
        }
        return null;
    };

    return (
        <div className="wrapper">
            <header>
                <NavigationBar />
            </header>

            <div className="content-wrapper">
                <div className="content-header">
                    <div className="container-fluid">
                        <h1 className="m-0">{t("orderManagement.pageTitle.orderManagement")}</h1>
                    </div>
                </div>

                <div className="content">
                    <div className="container-fluid">
                        <div className="mtk-orders-tabs" role="tablist" aria-label={t("orderManagement.list.filter", "Filter orders")}>
                            {TABS.map(({ key, label }) => (
                                <button
                                    key={key}
                                    type="button"
                                    role="tab"
                                    aria-selected={tab === key}
                                    className={`mtk-orders-tab${tab === key ? " is-active" : ""}`}
                                    onClick={() => chooseTab(key)}
                                >
                                    {label} <span className="mtk-orders-count">{counts[key]}</span>
                                </button>
                            ))}
                        </div>

                        <section className="mtk-orders-card">
                            <div className="mtk-orders-toolbar">
                                <label className="mtk-orders-inline">
                                    {t("orderManagement.list.show", "Show")}
                                    <select
                                        className="form-control"
                                        value={pageSize}
                                        onChange={(e) => {
                                            const n = Number(e.target.value);
                                            setPageSize(n);
                                            setPage(0);
                                            try { localStorage.setItem(PAGE_SIZE_KEY, String(n)); } catch (err) { /* private mode */ }
                                        }}
                                    >
                                        {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
                                    </select>
                                    {t("orderManagement.list.entries", "entries")}
                                </label>
                                <label className="mtk-orders-inline">
                                    {t("orderManagement.list.search", "Search:")}
                                    <input
                                        type="search"
                                        className="form-control mtk-orders-search"
                                        placeholder={t("orderManagement.list.searchPlaceholder", "Order ID, name, AWB")}
                                        value={search}
                                        onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                                    />
                                </label>
                            </div>

                            {courierActions && selected.size > 0 && (
                                <div className="mtk-orders-bulk" data-testid="bulk-bar">
                                    <strong>{t("orderManagement.list.selected", "{{count}} orders selected", { count: selected.size })}</strong>
                                    <span className="mtk-orders-spacer" />
                                    <button type="button" className="mtk-orders-btn is-primary" disabled={busy || !toCreate.length} onClick={() => createShipments(toCreate)}>
                                        {t("orderManagement.list.createShipments", "Create shipments")}
                                    </button>
                                    <button type="button" className="mtk-orders-btn" disabled={busy || !withLabel.length} onClick={() => downloadLabels(withLabel)}>
                                        {t("orderManagement.list.downloadLabels", "Download labels")}
                                    </button>
                                    <button type="button" className="mtk-orders-btn" disabled={busy || !forPickup.length} aria-expanded={pickupOpen} onClick={() => setPickupOpen((v) => !v)}>
                                        {t("orderManagement.list.schedulePickup", "Schedule pickup")}
                                    </button>
                                    {pickupOpen && forPickup.length > 0 && (
                                        <div className="mtk-orders-pickup">
                                            <label>
                                                {t("orderManagement.list.pickupDate", "Pickup date")}
                                                <select className="form-control" value={pickupDay} onChange={(e) => setPickupDay(e.target.value)}>
                                                    {days.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
                                                </select>
                                            </label>
                                            <label>
                                                {t("orderManagement.list.timeSlot", "Time slot")}
                                                <select className="form-control" value={pickupSlot} onChange={(e) => setPickupSlot(e.target.value)}>
                                                    {Object.entries(PICKUP_SLOTS).map(([v, label]) => <option key={v} value={v}>{label}</option>)}
                                                </select>
                                            </label>
                                            <button type="button" className="mtk-orders-btn is-primary" disabled={busy} onClick={schedulePickup}>
                                                {t("orderManagement.list.confirmPickup", "Schedule for {{count}} parcels", { count: forPickup.length })}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}

                            {notice && (
                                <div className={`alert alert-${notice.type} py-2`} role={notice.type === "danger" ? "alert" : "status"}>
                                    {notice.text}
                                    {notice.links && notice.links.length > 0 && (
                                        <ul className="mb-0 mt-1 pl-3" aria-label={t("orderManagement.list.labelLinks", "Labels")}>
                                            {notice.links.map((l) => (
                                                <li key={l.url}><a href={l.url} target="_blank" rel="noopener noreferrer">{t("orderManagement.list.labelFor", "Label for {{id}}", { id: l.label })}</a></li>
                                            ))}
                                        </ul>
                                    )}
                                    {notice.list && notice.list.length > 0 && <ul className="mb-0 mt-1 pl-3">{notice.list.map((line) => <li key={line}>{line}</li>)}</ul>}
                                </div>
                            )}
                            {loadError && (
                                <div className="alert alert-warning" role="alert">
                                    {t("orderManagement.list.loadFailed", "Couldn't load your orders.")}{" "}
                                    <button type="button" className="btn btn-link p-0 align-baseline" onClick={() => { setLoading(true); load(); }}>{t("orderManagement.list.tryAgain", "Try again")}</button>
                                </div>
                            )}

                            <div className="mtk-orders-scroll">
                                <table className="mtk-orders-table" aria-label={t("orderManagement.list.orders", "Orders")}>
                                    <thead>
                                        <tr>
                                            {courierActions && (
                                                <th scope="col" className="is-check">
                                                    <input type="checkbox" checked={allOnPage} onChange={togglePage} aria-label={t("orderManagement.list.selectAll", "Select all")} />
                                                </th>
                                            )}
                                            <th scope="col">{t("orderManagement.list.orderId", "Order ID")}</th>
                                            <th scope="col">{t("orderManagement.list.customer", "Customer")}</th>
                                            <th scope="col">{t("orderManagement.list.orderDate", "Order date")}</th>
                                            <th scope="col">{t("orderManagement.list.total", "Total")}</th>
                                            <th scope="col">{t("orderManagement.list.payment", "Payment")}</th>
                                            <th scope="col">{t("orderManagement.list.shippingStatus", "Shipping status")}</th>
                                            <th scope="col">{courierStore ? t("orderManagement.list.courierAwb", "Courier & AWB") : t("orderManagement.list.courierTracking", "Courier & tracking no.")}</th>
                                            <th scope="col">{t("orderManagement.list.deliverTo", "Deliver to")}</th>
                                            <th scope="col">{t("orderManagement.list.actions", "Actions")}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading && (
                                            <tr><td colSpan={courierStore ? 10 : 9} className="mtk-orders-empty">{t("orderManagement.list.loading", "Loading your orders…")}</td></tr>
                                        )}
                                        {!loading && !rows.length && (
                                            <tr><td colSpan={courierStore ? 10 : 9} className="mtk-orders-empty">{t("orderManagement.common.noRecordsFound")}</td></tr>
                                        )}
                                        {rows.map((order) => {
                                            const chip = shippingChip(order);
                                            const attention = orderAttention(order);
                                            const s = order.shipment || {};
                                            const courierName = hasShipment(order) ? (s.provider === "delhivery" ? "Delhivery" : order.courier_service) : order.courier_service;
                                            const trackingNo = hasShipment(order) ? s.awb : order.tracking_number;
                                            const shippingCharge = Number(order.shipping_charge) || 0;
                                            return (
                                                <React.Fragment key={order._id}>
                                                    <tr className={`${selected.has(order._id) ? "is-selected" : ""}${attention ? " has-note" : ""}`}>
                                                        {courierActions && (
                                                            <td className="is-check">
                                                                <input type="checkbox" checked={selected.has(order._id)} onChange={() => toggle(order._id)}
                                                                    aria-label={t("orderManagement.list.selectOrder", "Select order #{{id}}", { id: orderCode(order) })} />
                                                            </td>
                                                        )}
                                                        <td><button type="button" className="mtk-orders-id order-id-link" onClick={() => view(order)}>#{orderCode(order)}</button></td>
                                                        <td>{customerOf(order)}</td>
                                                        <td>{order.created_date ? new Date(order.created_date).toLocaleDateString("en-GB") : "—"}</td>
                                                        <td>
                                                            <strong>{rupees(totalOf(order))}</strong>
                                                            <span className="mtk-orders-sub">{shippingCharge > 0 ? `incl. ${rupees(shippingCharge)} shipping` : "free shipping"}</span>
                                                        </td>
                                                        <td className={isCod(order) ? "is-cod" : order.payment_status === "paid" ? "is-paid" : ""}>
                                                            {isCod(order) ? "COD" : order.payment_status === "paid" ? "Paid online" : "Pending"}
                                                        </td>
                                                        <td>
                                                            <span className={`mtk-order-chip mtk-order-chip--${chip.tone} is-small`}>{chip.label}</span>
                                                            <span className="mtk-orders-sub">{statusDetail(order, courierStore)}</span>
                                                        </td>
                                                        <td>
                                                            <span className="font-weight-bold">{courierName || (order.current_tracking_status === "delivered" ? "Own delivery" : "—")}</span>
                                                            <span className="mtk-orders-sub is-mono">{trackingNo || ""}</span>
                                                        </td>
                                                        <td>{deliverTo(order) || "—"}</td>
                                                        <td>
                                                            <span className="mtk-orders-actions">
                                                                {actionFor(order)}
                                                                <button type="button" className="mtk-orders-btn is-plain" onClick={() => view(order)}>{t("orderManagement.list.view", "View")}</button>
                                                            </span>
                                                        </td>
                                                    </tr>
                                                    {attention && (
                                                        <tr className="mtk-orders-note-row">
                                                            <td colSpan={courierStore ? 10 : 9}>
                                                                <div className={`mtk-orders-note is-${attention.tone}`} role="note">
                                                                    <svg aria-hidden="true" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M12 7v6M12 17h.01" /></svg>
                                                                    {attention.text}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )}
                                                </React.Fragment>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            <div className="mtk-orders-pager">
                                <span>
                                    {shown.length
                                        ? t("orderManagement.list.showing", "Showing {{from}} to {{to}} of {{total}} entries", { from: current * pageSize + 1, to: current * pageSize + rows.length, total: shown.length })
                                        : t("orderManagement.list.showingNone", "Showing 0 entries")}
                                </span>
                                <span className="mtk-orders-pages">
                                    <button type="button" disabled={current === 0} onClick={() => setPage(current - 1)}>{t("orderManagement.list.previous", "Previous")}</button>
                                    <span className="is-current" aria-current="page">{current + 1}</span>
                                    <button type="button" disabled={current >= pages - 1} onClick={() => setPage(current + 1)}>{t("orderManagement.list.next", "Next")}</button>
                                </span>
                            </div>
                        </section>
                    </div>
                </div>
            </div>
            <Footer />
        </div>
    );
}

export default OrderManagement;
